import {
  type IntegralResult,
  type Integrand,
  integrate,
  totalArea,
} from "@/lib/tool-kit/calculus/integrate";

/** The integral, plus the unsigned area when the integrand changes sign. */
export interface IntegralReport {
  net: IntegralResult;
  /** ∫|f|, only when f takes both signs on the interval; otherwise null. */
  total: IntegralResult | null;
  /** True when sampled values of f are both positive and negative. */
  changesSign: boolean;
}

/** Sample f across the finite part of [a, b] and report whether it takes both signs. */
export function changesSign(f: Integrand, a: number, b: number): boolean {
  const [lo, hi] =
    Number.isFinite(a) && Number.isFinite(b) ? [a, b] : plotRange(a, b);
  let positive = false;
  let negative = false;
  for (let i = 0; i <= 400; i += 1) {
    const y = f(lo + ((hi - lo) * i) / 400);
    positive = positive || y > 1e-12;
    negative = negative || y < -1e-12;
  }
  return positive && negative;
}

export function integralReport(
  f: Integrand,
  a: number,
  b: number
): IntegralReport {
  const net = integrate(f, a, b);
  const signs = changesSign(f, a, b);
  const total = signs && Number.isFinite(net.value) ? totalArea(f, a, b) : null;
  return { net, total, changesSign: signs };
}

/** The x-range to draw: the interval itself, or a window of 10 units on an infinite side. */
export function plotRange(a: number, b: number): [number, number] {
  const lowerFinite = Number.isFinite(a);
  const upperFinite = Number.isFinite(b);
  if (lowerFinite && upperFinite) {
    const pad = (b - a) * 0.08 || 1;
    return [a - pad, b + pad];
  }
  if (lowerFinite) {
    return [a - 1, a + 10];
  }
  if (upperFinite) {
    return [b - 10, b + 1];
  }
  return [-10, 10];
}
